import { getCustomFilterState, getFilterState, updateStore } from "./ReduxHelper";
import ErrorService from "../services/ErrorService";
import FieldNames from "../defaults/FieldNames.json";
import FilterFieldsOrdering from "../defaults/FilterFieldsOrdering.json";
import { getEndOfDay, getStartOfDay } from "./DateHelper";
import { getBaseFilterString } from "./QueryHelper";
import { setDefinitionExpression, setFilterExpression } from "../redux/slices/filterSlice";
import store from "../redux/store";

export const getMinMaxClause = (min, max, fieldName) => {

  const minClause = (min) ? `(${fieldName} >= ${min})` : null;
  const maxClause = (max) ? `(${fieldName} <= ${max})` : null;

  if (minClause && maxClause) 
  {
    return " (" + minClause + " AND " + maxClause + ") ";
  }

  if (minClause && !maxClause) 
  {
    return " (" + minClause + ") ";
  }

  if (!minClause && maxClause) 
  {
    return " (" + maxClause + ") ";
  }

  if (!minClause && !maxClause) 
  {
    return null;
  }
}

export const getDatasetFilter = (selectedDataset) => {
  let datasetWhere = null;
  if(selectedDataset && selectedDataset !== FieldNames.datasets.both)
  {
    if(selectedDataset === FieldNames.datasets.fuels)
    {
      datasetWhere = `((${FieldNames.featureFields.entityType} IS NULL) OR (${FieldNames.featureFields.entityType} = '${FieldNames.internalEntityTypes.fuels}'))`;
    }
    else if(selectedDataset === FieldNames.datasets.postfire)
    {
      datasetWhere = `(${FieldNames.featureFields.entityType} = '${FieldNames.internalEntityTypes.postFire}')`;
    }
  }
  return datasetWhere;
}

const getCalculatedAreaClause = (min, max, unitType) => {

  const minClause = (min) ? `(${FieldNames.featureFields.calculatedArea} >= ${min})` : null;
  const maxClause = (max) ? `(${FieldNames.featureFields.calculatedArea} <= ${max})` : null;
  const minMaxClause = `(${minClause ?? ''}${minClause && maxClause ? ' AND ' : ''}${maxClause ?? ''})`;
  const baseUnitTypeClause = `(${FieldNames.featureFields.calculatedAreaUnit} = '${unitType}')`;

  if(!minClause && !maxClause && !unitType)
  {
    return null;
  }

  if(!minClause && !maxClause && unitType)
  {
    return baseUnitTypeClause;
  }

  if(unitType)
  {
    if(unitType === FieldNames.areaUnitTypes.feet)
    {
      const feetClause = `(${minMaxClause} AND ${baseUnitTypeClause})`;
      const milesMinValue = min ? +((min / 5280).toFixed(1)) : null;
      const milesMaxValue = max ? +((max / 5280).toFixed(1)) : null;
      const milesValueClause = (milesMinValue || milesMaxValue) ? getCalculatedAreaClause(milesMinValue, milesMaxValue, null) : null;
      const milesClause = milesValueClause ? `${milesValueClause} AND (${FieldNames.featureFields.calculatedAreaUnit} = '${FieldNames.areaUnitTypes.miles}')` : "";
      const combinedClause = `(${feetClause}${milesClause ? (' OR ' + milesClause) : ''})`;
      return combinedClause;
    }
    else if(unitType === FieldNames.areaUnitTypes.miles)
    {
      const milesClause = `(${minMaxClause} AND ${baseUnitTypeClause})`;
      const feetMinValue = min ? +((min * 5280).toFixed(1)) : null;
      const feetMaxValue = max ? +((max * 5280).toFixed(1)) : null;
      const feetValueClause = (feetMinValue || feetMaxValue) ? getCalculatedAreaClause(feetMinValue, feetMaxValue, null) : null;
      const feetClause = feetValueClause ? `${feetValueClause} AND (${FieldNames.featureFields.calculatedAreaUnit} = '${FieldNames.areaUnitTypes.feet}')` : "";
      const combinedClause = `(${milesClause}${feetClause ? (' OR ' + feetClause) : ''})`;
      return combinedClause;
    }
    else // Acres
    {
      const acresClause = `(${minMaxClause} AND ${baseUnitTypeClause})`;
      return acresClause;
    }
  }
  else {
    return minMaxClause;
  }
}

// Note: Cannot be called from Redux slices
export const updateFilterAndDefinitionExpression = () => {
  const expressionString = getFilterExpressionString();
  updateStore(setFilterExpression, expressionString);
  updateStore(setDefinitionExpression, expressionString);
  return expressionString;
}

// Note: Cannot be called from Redux slices
export const updateFilterExpression = (activeDefinitionExpression=null) => {
  let expressionString = getFilterExpressionString();
  
  if(activeDefinitionExpression){
    expressionString = activeDefinitionExpression
  } 

  updateStore(setFilterExpression, expressionString);
  return expressionString;
}

// Note: Cannot be called from Redux slices
const getFilterExpressionString = () => {
  let expressionString;
  const tempFilters = getFilterState();
  if(tempFilters?.isBasicFilterActive)
  {
    expressionString = getBasicFilterExpression(tempFilters);
  }
  else
  {
    const customFilterState = getCustomFilterState();
    expressionString = getCustomFilterExpression(customFilterState);
  }
  return expressionString;
}

/**
 * TODO: Refactor this function to not be called from within redux reducer.
 * NOTE: This function is only called from within the Redux reducer in filterSlice.
 * It should be refactored - as side effects are not allowed in Redux reducers.
 * At this time, a 'SYSTEM' username is passed in for potential error handling
 * in basicFilterExpression(), and used to bypass a store read from within
 * getCustomFilterExpression(). The solution is hacky - but the existing implementation
 * is also hacky. Should be addressed in a future refactor.
 *
 * The correct solution is to eliminate side effects in Redux reducers. For this chain,
 * that means calling getFilterExpressionString() at calling points of setDataset(), and not
 * from within the reducer.
 */
export const getFilterExpressionStringFromRedux = (nifcUserName, state) => {
  let expressionString;
  const filterState = state;
  if(filterState?.isBasicFilterActive)
  {
    // Send 'SYSTEM' username through pipeline in case of error within getBasicFilterExpression()
    const systemUserNameForErrorHandling = nifcUserName
    expressionString = getBasicFilterExpression(filterState, systemUserNameForErrorHandling);
  }
  else
  {
    const customFilterState = state?.customFilter;
    // Manually bypass store read in getCustomFilterExpression(). When getCustomFilterExpression() is called
    // from outside a reducer (all other instances besides getFilter...FromRedux(), the store is accessible
    // downstream in getCustomFilterExpression())
    const bypassStoreRead = true
    expressionString = getCustomFilterExpression(customFilterState, bypassStoreRead);
  }
  return expressionString;
}

// TODO: Refactor this function to not be called from getFilterExpressionStringFromRedux() chain.
const getCustomFilterExpression = (customFilter, bypassStoreRead = false) => {
  // Only access userInfo from store when called from outside the getFilterExpressionStringFromRedux() chain.
  // When bypassStoreRead is true (called from Redux chain), skip store access and user-based dataset logic.
  const userInfo = bypassStoreRead ? null : (store.getState().globalContext.userInfo ?? null);

  const baseString = getBaseFilterString();
  let expr = baseString;
  //Can't do the check for single quotes here.  Must be done as the expression is built
  if(customFilter?.activeFilter){
    let storedDataset = customFilter?.activeFilterState?.Dataset;
    if(userInfo && typeof userInfo === 'object')
    {
      const isUserPostfire = userInfo[FieldNames.userFields.isPostFire] === FieldNames.yesNo.yes;
      const isUserFuels = userInfo[FieldNames.userFields.isFuels] === FieldNames.yesNo.yes;
      if(isUserFuels && !isUserPostfire)
      {
        storedDataset = FieldNames.datasets.fuels;
      }
      else if(!isUserFuels && isUserPostfire)
      {
        storedDataset = FieldNames.datasets.postfire;
      }
    }
    const datasetFilter = getDatasetFilter(storedDataset);
    expr = `${customFilter.activeFilter}` + (datasetFilter ? ` AND ${datasetFilter}` : "");
  }
  expr = expr.replaceAll('&lt;', '<').replaceAll('&gt;', '>');
  return expr;
}

// TODO: Refactor this function to not be called from getFilterExpressionStringFromRedux() chain.
const getBasicFilterExpression = (filters, userInfoOverride = null) => {
  // Only access userInfo from store when called from outside the getFilterExpressionStringFromRedux() chain.
  // If called from Redux, value is always 'SYSTEM'
  const userInfo = userInfoOverride ?? store.getState().globalContext.userInfo ?? 'UNKNOWN';

  try
  {                  
    let terms = [];
    if(!filters)
    {
      throw new Error("Unable to find filter slice");
    }
    const selectedDataset = filters.dataset;

    // From here, filters should have the redux filter slice
    const minInitiationDateValue = filters.minInitiationDate ? new Date(filters.minInitiationDate) : null;
    const minInitiationDateString = minInitiationDateValue ? getStartOfDay(minInitiationDateValue) : null;
    const minInitiationDate = (minInitiationDateString) ? `(${FieldNames.featureFields.initiationDate} >= date '${minInitiationDateString}')` : null;

    const maxInitiationDateValue = filters.maxInitiationDate ? new Date(filters.maxInitiationDate) : null;
    const maxInitiationDateString = maxInitiationDateValue ? getEndOfDay(maxInitiationDateValue) : null;
    const maxInitiationDate = (maxInitiationDateString) ? `(${FieldNames.featureFields.initiationDate} <= date '${maxInitiationDateString}')` : null;

    const minCompletionDateValue = filters.minCompletionDate;
    const minCompletionDateString = minCompletionDateValue ? getStartOfDay(minCompletionDateValue) : null;
    const minCompletionDate = (minCompletionDateString) ? `(${FieldNames.featureFields.completionDate} >= date '${minCompletionDateString}')` : null;

    const maxCompletionDateValue = filters.maxCompletionDate;
    const maxCompletionDateString = maxCompletionDateValue ? getEndOfDay(maxCompletionDateValue) : null;
    const maxCompletionDate = (maxCompletionDateString) ? `(${FieldNames.featureFields.completionDate} <= date '${maxCompletionDateString}')` : null;

    const basicFilterFeatureSelection = filters.filterFeatureSelection;
    const featureSelection = (basicFilterFeatureSelection && basicFilterFeatureSelection.length > 0) ? `( ${FieldNames.featureFields.class} IN ('${basicFilterFeatureSelection.join("', '")}') )` : null;

    const minDurabilityValue = filters.minDurability;
    const maxDurabilityValue = filters.maxDurability;
    const durabilityWhere = getMinMaxClause(minDurabilityValue, maxDurabilityValue, FieldNames.featureFields.durability);

    const prioritiesValue = filters.priorities;
    const priorityWhere = (prioritiesValue && prioritiesValue.length > 0) ? `( ${FieldNames.featureFields.priority} IN ('${prioritiesValue.join("', '")}') )` : null;
  
    const minCalculatedAreaValue = filters.minCalculatedArea;
    const maxCalculatedAreaValue = filters.maxCalculatedArea;
    const calculatedAreaTypeValue = filters.calculatedAreaType;
    const calcAreaWhere = getCalculatedAreaClause(minCalculatedAreaValue, maxCalculatedAreaValue, calculatedAreaTypeValue);

    const minEstGrantsFixedCost = filters.minEstGrantsFixedCost;
    const maxEstGrantsFixedCost = filters.maxEstGrantsFixedCost;
    const estGrantsCostWhere = getMinMaxClause(minEstGrantsFixedCost, maxEstGrantsFixedCost, FieldNames.featureFields.estimatedGrantsFixedCost);

    const minEstPersonnelCost = filters.minEstPersonnelCost;
    const maxEstPersonnelCost = filters.maxEstPersonnelCost;
    const estPersonnelCostWhere = getMinMaxClause(minEstPersonnelCost, maxEstPersonnelCost, FieldNames.featureFields.estimatedPersonnelCost);

    const minEstContractualCost = filters.minEstContractualCost;
    const maxEstContractualCost = filters.maxEstContractualCost;
    const estContractualCostWhere = getMinMaxClause(minEstContractualCost, maxEstContractualCost, FieldNames.featureFields.estimatedContractualCost);

    const minEstOtherCost = filters.minEstOtherCost;
    const maxEstOtherCost = filters.maxEstOtherCost;
    const estOtherCostWhere = getMinMaxClause(minEstOtherCost, maxEstOtherCost, FieldNames.featureFields.estimatedOtherCost);

    const minEstAssetCost = filters.maxEstAssetCost;
    const maxEstAssetCost = filters.maxEstAssetCost;
    const estAssetCostWhere = getMinMaxClause(minEstAssetCost, maxEstAssetCost, FieldNames.featureFields.estimatedAssetCost);

    const minEstTotalCost = filters.minEstTotalCost;
    const maxEstTotalCost = filters.maxEstTotalCost;
    const estTotalCostWhere = getMinMaxClause(minEstTotalCost, maxEstTotalCost, FieldNames.featureFields.estimatedTotalCost);

    const selectedProjectValues = filters.filterProjectValue;
    const projectNameWhere = (selectedProjectValues?.length > 0) ? `( ${FieldNames.featureFields.projectName} IN ('${selectedProjectValues?.join("', '")}') )` : null;

    const selectedProjectDiscoveryDateValues = filters.projectDiscoveryDates;
    const formattedProjectDiscoveryDateQueries = selectedProjectDiscoveryDateValues?.map(date => {
      const discoveryDateVal = date ? new Date(date) : null;
      const discoveryDateString = discoveryDateVal ? getStartOfDay(discoveryDateVal) : null;  
      const discoveryDateQuery = discoveryDateString ? `(${FieldNames.featureFields.fireDiscoveryDate} = date '${discoveryDateString}')` : null;
      return discoveryDateQuery;
    }) ?? [];
    const finalDiscoveryDateQueryString = (formattedProjectDiscoveryDateQueries?.length > 0) ? "(" + formattedProjectDiscoveryDateQueries.join(" OR ") + ")" : null;
    const projectDiscoveryDateWhere = (formattedProjectDiscoveryDateQueries?.length > 0) ? `${finalDiscoveryDateQueryString}` : null;

    const selectedProjectIDValues = filters.projectIDs;
    const projectIDWhere = (selectedProjectIDValues?.length > 0) ? `( ${FieldNames.featureFields.projectID} IN (${selectedProjectIDValues?.join(", ")}) )` : null;

    const selectedProjectFireCodeValues = filters.projectFireCodes;
    const projectFireCodeWhere = (selectedProjectFireCodeValues?.length > 0) ? `( ${FieldNames.featureFields.fireCode} IN ('${selectedProjectFireCodeValues?.join("', '")}') )` : null;

    const postFireCategoryValue = filters.postFireCategories;
    const postFireCategoryWhere = selectedDataset === FieldNames.datasets.postfire ? 
      ((postFireCategoryValue?.length > 0) ? `((${FieldNames.featureFields.category} IN ('${postFireCategoryValue.join("', '")}')))` : null) : 
      ((postFireCategoryValue?.length > 0) ? `((${FieldNames.featureFields.category} IN ('${postFireCategoryValue.join("', '")}')) AND (${FieldNames.featureFields.entityType} = '${FieldNames.internalEntityTypes.postFire}'))` : null);

    const fuelsCategoryValue = filters.fuelsCategories;
    const fuelsCategoryWhere = selectedDataset === FieldNames.datasets.fuels ? 
      ((fuelsCategoryValue?.length > 0) ? `((${FieldNames.featureFields.category} IN ('${fuelsCategoryValue.join("', '")}')))` : null) : 
      ((fuelsCategoryValue?.length > 0) ? `((${FieldNames.featureFields.category} IN ('${fuelsCategoryValue.join("', '")}')) AND (${FieldNames.featureFields.entityType} = '${FieldNames.internalEntityTypes.fuels}'))` : null);

    let categoryWhere = null;
    if(fuelsCategoryWhere && postFireCategoryWhere){
      categoryWhere = "(" + fuelsCategoryWhere + " OR " + postFireCategoryWhere + ")";
    } else if(fuelsCategoryWhere && !postFireCategoryWhere){
      categoryWhere = fuelsCategoryWhere;
    } else if(!fuelsCategoryWhere && postFireCategoryWhere){
      categoryWhere = postFireCategoryWhere;
    }

    const postFireTypeValue = filters.postFireTypes;
    const postFireTypeWhere = selectedDataset === FieldNames.datasets.postfire ? 
      ((postFireTypeValue?.length > 0) ? `((${FieldNames.featureFields.type} IN ('${postFireTypeValue.join("', '")}')))` : null) : 
      ((postFireTypeValue?.length > 0) ? `((${FieldNames.featureFields.type} IN ('${postFireTypeValue.join("', '")}')) AND (${FieldNames.featureFields.entityType} = '${FieldNames.internalEntityTypes.postFire}'))` : null);

    const fuelsTypeValue = filters.fuelsTypes;
    const fuelsTypeWhere = selectedDataset === FieldNames.datasets.fuels ? 
      ((fuelsTypeValue?.length > 0) ? `((${FieldNames.featureFields.type} IN ('${fuelsTypeValue.join("', '")}')))` : null) : 
      ((fuelsTypeValue?.length > 0) ? `((${FieldNames.featureFields.type} IN ('${fuelsTypeValue.join("', '")}')) AND (${FieldNames.featureFields.entityType} = '${FieldNames.internalEntityTypes.fuels}'))` : null);

    let typeWhere = null;
    if(fuelsTypeWhere && postFireTypeWhere){
      typeWhere = "(" + fuelsTypeWhere + " OR " + postFireTypeWhere + ")";
    } else if(fuelsTypeWhere && !postFireTypeWhere){
      typeWhere = fuelsTypeWhere;
    } else if(!fuelsTypeWhere && postFireTypeWhere){
      typeWhere = postFireTypeWhere;
    }

    let isRTRLWhere = null;
    const isRTRLValue = filters.isRTRL
    let tempIsRTRLValue = isRTRLValue;
    if(tempIsRTRLValue && tempIsRTRLValue.length > 0 && tempIsRTRLValue.includes(FieldNames.yesNo.no)){
      isRTRLWhere = `(${FieldNames.featureFields.isRTRL} IS NULL)`;
      tempIsRTRLValue = tempIsRTRLValue.filter(x => x !== 'null');
    }
    if(tempIsRTRLValue && tempIsRTRLValue.length > 0){
      if(isRTRLWhere) {
        isRTRLWhere = `((${isRTRLWhere}) OR ( ${FieldNames.featureFields.isRTRL} IN ('${tempIsRTRLValue.join("', '")}') ))`;
      } else {
        isRTRLWhere = `(( ${FieldNames.featureFields.isRTRL} IN ('${tempIsRTRLValue.join("', '")}') ))`;
      }
    }

    let isContractWhere = null;
    const isContractValue = filters.isContract
    let tempIsContractValue = isContractValue;
    if(tempIsContractValue && tempIsContractValue.length > 0 && tempIsContractValue.includes(FieldNames.yesNo.no)){
      isContractWhere = `(${FieldNames.featureFields.isContract} IS NULL)`;
      tempIsContractValue = tempIsContractValue.filter(x => x !== 'null');
    }
    if(tempIsContractValue && tempIsContractValue.length > 0){
      if(isContractWhere) {
        isContractWhere = `((${isContractWhere}) OR ( ${FieldNames.featureFields.isContract} IN ('${tempIsContractValue.join("', '")}') ))`;
      } else {
        isContractWhere = `(( ${FieldNames.featureFields.isContract} IN ('${tempIsContractValue.join("', '")}') ))`;
      }
    }

    let isAgreementWhere = null;
    const isAgreementValue = filters.isAgreement
    let tempIsAgreementValue = isAgreementValue;
    if(tempIsAgreementValue && tempIsAgreementValue.length > 0 && tempIsAgreementValue.includes(FieldNames.yesNo.no)){
      isAgreementWhere = `(${FieldNames.featureFields.isAgreement} IS NULL)`;
      tempIsAgreementValue = tempIsAgreementValue.filter(x => x !== 'null');
    }
    if(tempIsAgreementValue && tempIsAgreementValue.length > 0){
      if(isAgreementWhere) {
        isAgreementWhere = `((${isAgreementWhere}) OR ( ${FieldNames.featureFields.isAgreement} IN ('${tempIsAgreementValue.join("', '")}') ))`;
      } else {
        isAgreementWhere = `(( ${FieldNames.featureFields.isAgreement} IN ('${tempIsAgreementValue.join("', '")}') ))`;
      }
    }

    const wbsIDSelectedValues = filters.wbsIds;
    const wbsidWhere = (wbsIDSelectedValues?.length > 0) ? `( ${FieldNames.featureFields.wbsID} IN ('${wbsIDSelectedValues.join("', '")}') )` : null;

    const statusValue = filters.status;
    const statusWhere = (statusValue?.length > 0) ? `(${FieldNames.featureFields.status} IN ('${statusValue.join("', '")}') )` : null;

    let isCarryoverWhere = null;
    const isCarryoverValue = filters.isCarryover;
    let tempIsCarryoverValue = isCarryoverValue;
    if(tempIsCarryoverValue && tempIsCarryoverValue.length > 0 && tempIsCarryoverValue.includes(FieldNames.yesNo.no)){
      isCarryoverWhere = `(${FieldNames.featureFields.isCarryover} IS NULL)`; 
      tempIsCarryoverValue = tempIsCarryoverValue.filter(x => x !== 'null');
    }
    if(tempIsCarryoverValue && tempIsCarryoverValue.length > 0){
      if(isCarryoverWhere) {
        isCarryoverWhere = `((${isCarryoverWhere}) OR ( ${FieldNames.featureFields.isCarryover} IN ('${tempIsCarryoverValue.join("', '")}') ))`;
      } else {
        isCarryoverWhere = `(( ${FieldNames.featureFields.isCarryover} IN ('${tempIsCarryoverValue.join("', '")}') ))`;
      }
    }

    const stateValue = filters.states;
    const stateCodes = stateValue.map((eaState) => eaState.code);
    const statesWhere = (stateCodes?.length > 0) ? `( ${FieldNames.featureFields.state} IN ('${stateCodes.join("', '")}') )` : null;

    const countyValue = filters.counties;
    const countyCodes = countyValue.map((eaCounty) => eaCounty.code);
    const countiesWhere = (countyCodes?.length > 0) ? `( ${FieldNames.featureFields.county} IN ('${countyCodes.join("', '")}') )` : null;

    let fundingSourceWhere = null;
    let finalWhere = null;
    const fundingSourceValue = filters.fundingSources;
    if(fundingSourceValue && fundingSourceValue.length > 0)
    {
      let baseWhere = '(';
      for(let i = 0; i < fundingSourceValue.length; i++)
      {
        let orStr = i > 0 ? ' OR ' : '';
        let interimWhere = `(${FieldNames.featureFields.fundingSource} = '${fundingSourceValue[i]}'
                             OR ${FieldNames.featureFields.fundingSource} LIKE '%|${fundingSourceValue[i]}' 
                             OR ${FieldNames.featureFields.fundingSource} LIKE '${fundingSourceValue[i]}|%'
                             OR ${FieldNames.featureFields.fundingSource} LIKE '%|${fundingSourceValue[i]}|%')`;
        baseWhere = baseWhere + orStr + interimWhere;
      }
      finalWhere = baseWhere + ')';
    }
    fundingSourceWhere = finalWhere;

    let unitWhereValueCodes = [];
    let unitWhere = null;
    const fundingUnitValue = filters.fundingUnit;
    if(fundingUnitValue && fundingUnitValue.length > 0){
      //Need to check for single quotes
      unitWhereValueCodes = fundingUnitValue.map(x => (x.Unit && x.Unit.includes("'")) ? x.Unit.replaceAll("'", "''") : x.Unit);
      let finalWhere = null;
      if(unitWhereValueCodes && unitWhereValueCodes.length > 0)
      {
        let baseWhere = '(';
        for(let i = 0; i < unitWhereValueCodes.length; i++)
        {
          let orStr = i > 0 ? ' OR ' : '';
          let interimWhere = `(${FieldNames.featureFields.fundingUnit} = '${unitWhereValueCodes[i]}'
                              OR ${FieldNames.featureFields.fundingUnit} LIKE '%|${unitWhereValueCodes[i]}' 
                              OR ${FieldNames.featureFields.fundingUnit} LIKE '${unitWhereValueCodes[i]}|%'
                              OR ${FieldNames.featureFields.fundingUnit} LIKE '%|${unitWhereValueCodes[i]}|%')`;
          baseWhere = baseWhere + orStr + interimWhere;
        }
        finalWhere = baseWhere + ')';
      }
      unitWhere = finalWhere;
    }

    let subUnitWhereValueCodes = [];
    let subUnitWhere = null;
    const fundingSubUnitValue = filters.fundingSubUnit;
    if(fundingSubUnitValue && fundingSubUnitValue.length > 0){
      //Need to check for single quotes
      subUnitWhereValueCodes = fundingSubUnitValue.map(x => (x.SubUnit && x.SubUnit.includes("'")) ? x.SubUnit.replaceAll("'", "''") : x.SubUnit);
      let finalWhere = null;
      if(subUnitWhereValueCodes && subUnitWhereValueCodes.length > 0)
      {
        let baseWhere = '(';
        for(let i = 0; i < subUnitWhereValueCodes.length; i++)
        {
          let orStr = i > 0 ? ' OR ' : '';
          let interimWhere = `(${FieldNames.featureFields.fundingSubUnit} = '${subUnitWhereValueCodes[i]}'
                              OR ${FieldNames.featureFields.fundingSubUnit} LIKE '%|${subUnitWhereValueCodes[i]}' 
                              OR ${FieldNames.featureFields.fundingSubUnit} LIKE '${subUnitWhereValueCodes[i]}|%'
                              OR ${FieldNames.featureFields.fundingSubUnit} LIKE '%|${subUnitWhereValueCodes[i]}|%')`;
          baseWhere = baseWhere + orStr + interimWhere;
        }
        finalWhere = baseWhere + ')';
      }
      subUnitWhere = finalWhere;
    }

    let tribeWhereValueCodes = [];
    let tribeWhere = null;
    const fundingTribeValue = filters.fundingTribe;
    if(fundingTribeValue && fundingTribeValue.length > 0){
      //Need to check for single quotes
      tribeWhereValueCodes = fundingTribeValue.map(x => (x[FieldNames.units.tribeFullName] && x[FieldNames.units.tribeFullName].includes("'")) ? x[FieldNames.units.tribeFullName].replaceAll("'", "''") : x[FieldNames.units.tribeFullName]);
      let finalWhere = null;
      if(tribeWhereValueCodes && tribeWhereValueCodes.length > 0)
      {
        let baseWhere = '(';
        for(let i = 0; i < tribeWhereValueCodes.length; i++)
        {
          let orStr = i > 0 ? ' OR ' : '';
          let interimWhere = `(${FieldNames.featureFields.fundingTribe} = '${tribeWhereValueCodes[i]}'
                              OR ${FieldNames.featureFields.fundingTribe} LIKE '%|${tribeWhereValueCodes[i]}' 
                              OR ${FieldNames.featureFields.fundingTribe} LIKE '${tribeWhereValueCodes[i]}|%'
                              OR ${FieldNames.featureFields.fundingTribe} LIKE '%|${tribeWhereValueCodes[i]}|%')`;
          baseWhere = baseWhere + orStr + interimWhere;
        }
        finalWhere = baseWhere + ')';
      }
      tribeWhere = finalWhere;
    }
    
    let unitTypeWhereValueCodes = [];
    let unitTypeWhere = null;
    const fundingUnitTypeValue = filters.fundingUnitType;
    if(fundingUnitTypeValue && fundingUnitTypeValue.length > 0){
      unitTypeWhereValueCodes = fundingUnitTypeValue.map(x => x.UnitType);
      let finalWhere = null;
      if(unitTypeWhereValueCodes && unitTypeWhereValueCodes.length > 0)
      {
        let baseWhere = '(';
        for(let i = 0; i < unitTypeWhereValueCodes.length; i++)
        {
          let orStr = i > 0 ? ' OR ' : '';
          let interimWhere = `(${FieldNames.featureFields.fundingUnitType} = '${unitTypeWhereValueCodes[i]}'
                              OR ${FieldNames.featureFields.fundingUnitType} LIKE '%|${unitTypeWhereValueCodes[i]}' 
                              OR ${FieldNames.featureFields.fundingUnitType} LIKE '${unitTypeWhereValueCodes[i]}|%'
                              OR ${FieldNames.featureFields.fundingUnitType} LIKE '%|${unitTypeWhereValueCodes[i]}|%')`;
          baseWhere = baseWhere + orStr + interimWhere;
        }
        finalWhere = baseWhere + ')';
      }
      unitTypeWhere = finalWhere;
    }

    const fundingUnitTypeIgnoreNationalValue = filters.fundingUnitTypeIgnoreNational;
    const ignoreNationalUnitTypeSelection = (fundingUnitTypeIgnoreNationalValue) ? `((${FieldNames.featureFields.fundingUnitType} IS NULL) OR (${FieldNames.featureFields.fundingUnitType} NOT IN ('NATIONAL')))` : null;
    const ignoreContributedOnlyValue = filters.ignoreContributedOnly;
    const ignoreContributedOnlySelection = (ignoreContributedOnlyValue) ? `((${FieldNames.featureFields.fundingSourceProgram} IS NULL) OR ((${FieldNames.featureFields.fundingSourceProgram} IS NOT NULL) AND (${FieldNames.featureFields.fundingSourceProgram} <> 'Contributed')))` : null;

    let regionWhereValueCodes = [];
    let regionWhere = null;
    const fundingRegionValue = filters.fundingRegion;
    if(fundingRegionValue && fundingRegionValue.length > 0){
      //Need to check for single quotes
      regionWhereValueCodes = fundingRegionValue.map(x => (x.Region && x.Region.includes("'")) ? x.Region.replaceAll("'", "''") : x.Region);
      let finalWhere = null;
      if(regionWhereValueCodes && regionWhereValueCodes.length > 0)
      {
        let baseWhere = '(';
        for(let i = 0; i < regionWhereValueCodes.length; i++)
        {
          let orStr = i > 0 ? ' OR ' : '';
          let interimWhere = `(${FieldNames.featureFields.fundingRegion} = '${regionWhereValueCodes[i]}'
                              OR ${FieldNames.featureFields.fundingRegion} LIKE '%|${regionWhereValueCodes[i]}' 
                              OR ${FieldNames.featureFields.fundingRegion} LIKE '${regionWhereValueCodes[i]}|%'
                              OR ${FieldNames.featureFields.fundingRegion} LIKE '%|${regionWhereValueCodes[i]}|%')`;
          baseWhere = baseWhere + orStr + interimWhere;
        }
        finalWhere = baseWhere + ')';
      }
      regionWhere = finalWhere;
    }

    let agencyWhereValueCodes = [];
    let agencyWhere = null;
    const fundingAgencyValue = filters.fundingAgency;
    if(fundingAgencyValue && fundingAgencyValue.length > 0){
      //Need to check for single quotes
      agencyWhereValueCodes = fundingAgencyValue.map(x => (x.Agency && x.Agency.includes("'")) ? x.Agency.replaceAll("'", "''") : x.Agency);
      let finalWhere = null;
      if(agencyWhereValueCodes && agencyWhereValueCodes.length > 0)
      {
        let baseWhere = '(';
        for(let i = 0; i < agencyWhereValueCodes.length; i++)
        {
          let orStr = i > 0 ? ' OR ' : '';
          let interimWhere = `(${FieldNames.featureFields.fundingAgency} = '${agencyWhereValueCodes[i]}'
                              OR ${FieldNames.featureFields.fundingAgency} LIKE '%|${agencyWhereValueCodes[i]}' 
                              OR ${FieldNames.featureFields.fundingAgency} LIKE '${agencyWhereValueCodes[i]}|%'
                              OR ${FieldNames.featureFields.fundingAgency} LIKE '%|${agencyWhereValueCodes[i]}|%')`;
          baseWhere = baseWhere + orStr + interimWhere;
        }
        finalWhere = baseWhere + ')';
      }
      agencyWhere = finalWhere;
    }

    let departmentWhereValueCodes = [];
    let departmentWhere = null;
    const fundingDepartmentValue = filters.fundingDepartment;
    if(fundingDepartmentValue && fundingDepartmentValue.length > 0){
      //Need to check for single quotes
      departmentWhereValueCodes = fundingDepartmentValue.map(x => (x.Department && x.Department.includes("'")) ? x.Department.replaceAll("'", "''") : x.Department);
      let finalWhere = null;
      if(departmentWhereValueCodes && departmentWhereValueCodes.length > 0)
      {
        let baseWhere = '(';
        for(let i = 0; i < departmentWhereValueCodes.length; i++)
        {
          let orStr = i > 0 ? ' OR ' : '';
          let interimWhere = `(${FieldNames.featureFields.fundingDepartment} = '${departmentWhereValueCodes[i]}'
                              OR ${FieldNames.featureFields.fundingDepartment} LIKE '%|${departmentWhereValueCodes[i]}' 
                              OR ${FieldNames.featureFields.fundingDepartment} LIKE '${departmentWhereValueCodes[i]}|%'
                              OR ${FieldNames.featureFields.fundingDepartment} LIKE '%|${departmentWhereValueCodes[i]}|%')`;
          baseWhere = baseWhere + orStr + interimWhere;
        }
        finalWhere = baseWhere + ')';
      }
      departmentWhere = finalWhere;
    }
    

    let congressDistValueCodes = [];
    let congressDistWhere = null;
    const congressDistValue = filters.congressDistValue;
    if(congressDistValue && congressDistValue.length > 0){
      congressDistValueCodes = congressDistValue.map(x => x.code);
      congressDistWhere = (congressDistValueCodes?.length > 0) ? `( ${FieldNames.featureFields.congressionalDistrictNumber} IN ('${congressDistValueCodes.join("', '")}') )` : null;
    }

    const minInitiationFiscalYear = filters.minFiscalYear;
    const maxInitiationFiscalYear = filters.maxFiscalYear;
    const initiationFiscalYearWhere = getMinMaxClause(minInitiationFiscalYear, maxInitiationFiscalYear, FieldNames.featureFields.initiationFiscalYear);

    const initiationFiscalQuartersValue = filters.initiationFiscalQuarters;
    const initiationFiscalQuarterWhere = (initiationFiscalQuartersValue?.length > 0) ? `( ${FieldNames.featureFields.initiationFiscalQuarter} IN ('${initiationFiscalQuartersValue.join("', '")}') )` : null;

    const minCompletionFiscalYear = filters.minCompletionFiscalYear;
    const maxCompletionFiscalYear = filters.maxCompletionFiscalYear;
    const completionFiscalYearWhere = getMinMaxClause(minCompletionFiscalYear, maxCompletionFiscalYear, FieldNames.featureFields.completionFiscalYear);
    
    const completionFiscalQuartersValue = filters.completionFiscalQuarters;
    const completionFiscalQuarterWhere = (completionFiscalQuartersValue?.length > 0) ? `( ${FieldNames.featureFields.completionFiscalQuarter} IN ('${completionFiscalQuartersValue.join("', '")}') )` : null;

    const minObligationFiscalYear = filters.minObligationFiscalYear;
    const maxObligationFiscalYear = filters.maxObligationFiscalYear;
    const obligationFiscalYearWhere = getMinMaxClause(minObligationFiscalYear, maxObligationFiscalYear, FieldNames.featureFields.obligationFiscalYear);

    const isDepartmentApproved = filters.isDepartmentApproved;
    let departmentApprovalWhere = null;
    if(isDepartmentApproved === 'Yes'){
      departmentApprovalWhere = `(${FieldNames.featureFields.departmentApprovalDate} IS NOT NULL)`;
    } else if(isDepartmentApproved === 'No'){
      departmentApprovalWhere = `(${FieldNames.featureFields.departmentApprovalDate} IS NULL)`;
    }

    const isAgencyApproved = filters.isAgencyApproved;
    let agencyApprovalWhere = null;
    if(isAgencyApproved === 'Yes'){
      agencyApprovalWhere = `(${FieldNames.featureFields.agencyApprovalDate} IS NOT NULL)`;
    } else if(isAgencyApproved === 'No'){
      agencyApprovalWhere = `(${FieldNames.featureFields.agencyApprovalDate} IS NULL)`;
    }

    const isRegionApproved = filters.isRegionApproved;
    let regionApprovalWhere = null;
    if(isRegionApproved === 'Yes'){
      regionApprovalWhere = `(${FieldNames.featureFields.regionalApprovalDate} IS NOT NULL)`;
    } else if(isRegionApproved === 'No'){
      regionApprovalWhere = `(${FieldNames.featureFields.regionalApprovalDate} IS NULL)`;
    }

    const isLocalApproved = filters.isLocalApproved;
    let localApprovalWhere = null;
    if(isLocalApproved === 'Yes'){
      localApprovalWhere = `(${FieldNames.featureFields.localApprovalDate} IS NOT NULL)`;
    } else if(isLocalApproved === 'No'){
      localApprovalWhere = `(${FieldNames.featureFields.localApprovalDate} IS NULL)`;
    }

    const isInteragencySupport = filters.isInteragency;
    let isInteragencySupportWhere = null;
    if(isInteragencySupport === 'Yes'){
      isInteragencySupportWhere = `(${FieldNames.featureFields.isInteragency} = '${FieldNames.yesNo.yes}')`;
    } else if(isInteragencySupport === 'No'){
      isInteragencySupportWhere = `(${FieldNames.featureFields.isInteragency} IS NULL OR ${FieldNames.featureFields.isInteragency} = '${FieldNames.yesNo.no}')`;
    }

    const selectedNameValues = filters.names;
    const nameWhere = (selectedNameValues?.length > 0) ? `( ${FieldNames.featureFields.name} IN ('${selectedNameValues.join("', '")}') )` : null;

    let datasetWhere = null;
    datasetWhere = getDatasetFilter(selectedDataset);

    // Note: As of FUELS-887, these terms should roughly be added in a way that the filters that narrow data the most go first.
    //        If a new basic filter is added and a term added to the array below, be sure to add it to FilterFieldsOrdering
    let orderedTerms = [];
    //#region TermList
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.wbsID], value: wbsidWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.fundingFields.fundingSubUnit], value: subUnitWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.fundingFields.fundingTribe], value: tribeWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.fundingFields.fundingUnit], value: unitWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.state], value: statesWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.county], value: countiesWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.estimatedGrantsFixedCost], value: estGrantsCostWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.estimatedPersonnelCost], value: estPersonnelCostWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.estimatedContractualCost], value: estContractualCostWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.estimatedOtherCost], value: estOtherCostWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.estimatedAssetCost], value: estAssetCostWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.estimatedTotalCost], value: estTotalCostWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.calculatedArea], value: calcAreaWhere}); 
    terms.push({order: FilterFieldsOrdering[FieldNames.filterNames.minCompletionDate], value: minCompletionDate});
    terms.push({order: FilterFieldsOrdering[FieldNames.filterNames.maxCompletionDate], value: maxCompletionDate});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.completionFiscalYear], value: completionFiscalYearWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.completionFiscalQuarter], value: completionFiscalQuarterWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.obligationFiscalYear], value: obligationFiscalYearWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.durability], value: durabilityWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.congressionalDistrictNumber], value: congressDistWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.category], value: categoryWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.type], value: typeWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.project], value: selectedDataset!==FieldNames.datasets.fuels ? projectNameWhere : null});
    terms.push({order: FilterFieldsOrdering[FieldNames.filterNames.projectDiscoveryDates], value: selectedDataset!==FieldNames.datasets.fuels ? projectDiscoveryDateWhere : null});
    terms.push({order: FilterFieldsOrdering[FieldNames.filterNames.projectIDs], value: selectedDataset!==FieldNames.datasets.fuels ? projectIDWhere : null});
    terms.push({order: FilterFieldsOrdering[FieldNames.filterNames.projectFireCodes], value: selectedDataset!==FieldNames.datasets.fuels ? projectFireCodeWhere : null});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.isRTRL], value: isRTRLWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.isContract], value: isContractWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.isAgreement], value: isAgreementWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.departmentApprovalDate], value: departmentApprovalWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.agencyApprovalDate], value: agencyApprovalWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.regionalApprovalDate], value: regionApprovalWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.localApprovalDate], value: localApprovalWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.fundingFields.fundingSource], value: fundingSourceWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.filterNames.minInitiationDate], value: minInitiationDate});
    terms.push({order: FilterFieldsOrdering[FieldNames.filterNames.maxInitiationDate], value: maxInitiationDate});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.initiationFiscalYear], value: initiationFiscalYearWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.initiationFiscalQuarter], value: initiationFiscalQuarterWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.status], value: statusWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.isCarryover], value: isCarryoverWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.filterNames.ignoreNationalUnitTypeSelection], value: ignoreNationalUnitTypeSelection});
    terms.push({order: FilterFieldsOrdering[FieldNames.filterNames.ignoreContributedOnlySelection], value: ignoreContributedOnlySelection});
    terms.push({order: FilterFieldsOrdering[FieldNames.fundingFields.fundingUnitType], value: unitTypeWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.fundingFields.fundingRegion], value: regionWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.fundingFields.fundingAgency], value: agencyWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.fundingFields.fundingDepartment], value: departmentWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.priority], value: priorityWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.filterNames.featureTypeSelection], value: featureSelection});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.name], value: nameWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.entityType], value: datasetWhere});
    terms.push({order: FilterFieldsOrdering[FieldNames.featureFields.isInteragency], value: isInteragencySupportWhere});
    
    orderedTerms = terms.filter(x => x.value != null).sort((a,b) => a.order.order - b.order.order).map(x => x.value);
    //#endregion

    let finalWhereClause = getBaseFilterString();
    const validTerms = orderedTerms.filter(cause => cause !== null);
    let whereClause = (validTerms.length === 0) ? "" : "(" + validTerms.join(" AND ") + ")";
    if(whereClause){
      finalWhereClause = `(${whereClause})`;
    }
    return finalWhereClause;
  }
  catch(e)
  {
    ErrorService.addErrorLogEntryFromRedux(userInfo?.[FieldNames.userFields.nifcUsername], filters, e);
  }
}